import {
  CustomDialog,
  Header,
  Heading,
  Content,
  Footer,
  Text,
  Image,
  ButtonGroup,
  Button,
  CloseButton,
  SkeletonCollection,
  Skeleton,
  Collection,
  CardView,
  Card,
  CardPreview,
  CollectionCardPreview,
  ContextualHelp,
  Avatar,
  Badge,
} from "@react-spectrum/s2";
import { useAsyncList } from "react-stately";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import { getAvatarUrl, getVisualUrl } from "@app/utils";
import { usePersonas, personaImagesQueryOptions } from "@app/hooks/personas";

import ImagesIcon from "@react-spectrum/s2/icons/Images";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const PersonaCard = ({ item }) => (
  <Card id={item.id} textValue={item.name}>
    <CardPreview>
      <CollectionCardPreview>
        <Image src={item.url} alt={item.name} />
        {!item?.images?.length && (
          <Skeleton isLoading>
            <Image alt="Loading ..." />
            <Image alt="Loading ..." />
            <Image alt="Loading ..." />
          </Skeleton>
        )}
        {item?.images
          ?.filter((img) => !img.name.includes("isolated"))
          ?.slice(0, 3)
          ?.map((img) => (
            <Image key={img.id} src={img.url} alt={item.name} />
          ))}
      </CollectionCardPreview>
      {!!item?.imagesCount && (
        <Badge
          variant="neutral"
          size="XS"
          fillStyle="subtle"
          styles={style({
            position: "absolute",
            bottom: 8,
            insetEnd: 8,
          })}
        >
          <ImagesIcon />
          <Text>{item?.imagesCount}</Text>
        </Badge>
      )}
    </CardPreview>
    <Content>
      <Text
        slot="title"
        styles={style({
          display: "flex",
          alignItems: "center",
          gap: 8,
        })}
      >
        <Avatar src={item.url} name={item.name} size={24} />
        <Text
          styles={style({
            display: "flex",
            alignItems: "baseline",
            gap: 8,
          })}
        >
          <Text>{item.name}</Text>
          <Text styles={style({ font: "detail-sm" })}>
            {[item.role, item.age].join(", ")}
          </Text>
        </Text>
        <ContextualHelp variant="info" styles={style({ marginStart: "auto" })}>
          <Heading>
            <Text>{item.name}</Text>
            <Text styles={style({ font: "detail-sm", marginStart: 8 })}>
              {[item.role, item.age].join(", ")}
            </Text>
          </Heading>
          <Content>
            <Text>{item.description}</Text>
          </Content>
        </ContextualHelp>
      </Text>
    </Content>
  </Card>
);

export const PersonaSelectDialog = ({
  defaultSelectedKeys,
  onPrimaryAction,
}) => {
  const queryClient = useQueryClient();
  const personasQuery = usePersonas({ experimental_prefetchInRender: true });

  const listData = useAsyncList({
    getKey: (item) => item.id,
    async load() {
      try {
        const personaModules = await personasQuery.promise;

        // Fetch all avatar URLs in parallel
        const personasWithUrls = await Promise.all(
          personaModules.map(async (persona) => {
            let url;
            try {
              // Use getAvatarUrl which has proper fallback logic
              url = await getAvatarUrl(persona);
            } catch (error) {
              console.warn(
                `Failed to get avatar URL for ${persona.name}:`,
                error,
              );
              // Fallback to default avatar
              url =
                "https://mir-s3-cdn-cf.behance.net/project_modules/disp/690bc6105945313.5f84bfc9de488.png";
            }
            return {
              ...persona,
              url,
            };
          }),
        );

        // Fetch persona images asynchronously (don't block initial load)
        for (const persona of personasWithUrls) {
          queryClient
            .query(personaImagesQueryOptions(persona.id))
            .then(async ({ elements, total_count }) => {
              // Fetch all image URLs in parallel
              const imagesWithUrls = await Promise.all(
                (elements || []).map(async (element) => {
                  let imageUrl;
                  try {
                    // Use getVisualUrl with proper visual object structure
                    imageUrl = await getVisualUrl({
                      id: element?.thumbnail?.componentId,
                      libraryId: persona.libraryId || persona.id,
                      url: element?.url, // Use existing URL if available
                    });
                  } catch (error) {
                    console.warn(
                      `Failed to get image URL for ${element?.name}:`,
                      error,
                    );
                    // Fallback to default visual image
                    imageUrl =
                      "https://react-spectrum.adobe.com/s2/preview.c3b340d3.png";
                  }
                  return {
                    id: element?.thumbnail?.componentId,
                    name: element?.name,
                    url: imageUrl,
                  };
                }),
              );

              const update = {
                ...persona,
                images: imagesWithUrls,
                imagesCount: total_count,
              };

              // console.log("updated persona", persona, update);
              listData.update(persona.id, update);
            })
            .catch((error) => {
              console.error(
                `Failed to fetch images for ${persona.name}:`,
                error,
              );
            });
        }

        return {
          items: personasWithUrls,
          selectedKeys: new Set(defaultSelectedKeys),
        };
      } catch (error) {
        console.error("Failed to load personas", error);
        return { items: [], selectedKeys: new Set() };
      }
    },
  });

  return (
    <CustomDialog
      isDismissable
      styles={style({
        maxWidth: "90vw",
        width: {
          default: "full",
          sm: 1200,
        },
      })}
    >
      {({ close }) => (
        <div
          className={style({
            size: "full",
            display: "grid",
            gridTemplateAreas: [
              "header header header",
              ". . .",
              "content content content",
              ". . .",
              "footer footer buttonGroup",
            ],
            gridTemplateRows: ["auto", 16, "1fr", 32, "auto"],
          })}
        >
          <Header
            styles={style({
              gridArea: "header",
              display: "flex",
              alignItems: "center",
              gap: 12,
              font: "title-2xl",
              marginTop: 0,
            })}
          >
            <Text>Select persona</Text>
          </Header>
          <CardView
            selectionMode="single"
            selectionStyle="highlight"
            selectedKeys={listData.selectedKeys}
            onSelectionChange={listData.setSelectedKeys}
            loadingState={listData.loadingState}
            //   renderEmptyState={renderEmptyState}
            variant="quiet"
            styles={style({
              gridArea: "content",
              maxHeight: "60vh",
              overflowY: "auto",
            })}
          >
            <Collection items={listData.items}>
              {(item) => <PersonaCard item={item} />}
            </Collection>
            {listData.isLoading && (
              <SkeletonCollection>
                {() => (
                  <PersonaCard
                    item={{ id: Math.random(), name: "Loading...", url: "" }}
                  />
                )}
              </SkeletonCollection>
            )}
          </CardView>
          <Footer styles={style({ gridArea: "footer" })}></Footer>
          <ButtonGroup
            styles={style({
              gridArea: "buttonGroup",
              justifySelf: "end",
            })}
          >
            <Button
              variant="accent"
              isDisabled={
                listData.selectedKeys.size === 0 ||
                listData.selectedKeys.values().next().value ===
                  defaultSelectedKeys[0]
              }
              onPress={() => {
                onPrimaryAction?.(
                  listData.getItem(listData.selectedKeys.values().next().value),
                );
                close();
              }}
            >
              <Text>Select persona</Text>
            </Button>
          </ButtonGroup>
          <CloseButton
            styles={style({
              position: "absolute",
              top: 12,
              insetEnd: 12,
            })}
          />
        </div>
      )}
    </CustomDialog>
  );
};

PersonaCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    age: PropTypes.number,
    url: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    imagesCount: PropTypes.number,
  }).isRequired,
};

PersonaSelectDialog.propTypes = {
  defaultSelectedKeys: PropTypes.arrayOf(PropTypes.string),
  onPrimaryAction: PropTypes.func,
};
